import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, UploadCloud, Film, Check, Link as LinkIcon, ChevronUp, ChevronDown, Eye, EyeOff, Copy, Play, Languages, Lock, Infinity as InfinityIcon, Clock } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { useAdminT } from "./adminI18n";

const EMPTY = { title: "", subtitle: "", description: "", lessons: "", level: "", tag: "", image: "", title_en: "", subtitle_en: "", description_en: "", lessons_en: "", level_en: "", tag_en: "", price: 0, currency: "usd", access_days: 0, payment_link: "", special_price: 0 };

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm text-luxury-gray mb-2">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const CHUNK_SIZE = 8 * 1024 * 1024;

const uploadResilient = async (file, courseId, title, kind, onProgress) => {
  if (file.size <= 12 * 1024 * 1024 && !kind) {
    const data = new FormData();
    data.append("file", file);
    const res = await axios.post(`${API}/admin/videos/upload?course_id=${courseId}&title=${encodeURIComponent(title)}`, data, {
      headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      onUploadProgress: (ev) => onProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
    });
    return res.data;
  }
  const initRes = await axios.post(`${API}/admin/videos/upload/init`, null, {
    params: { course_id: courseId, title, kind: kind || undefined, filename: file.name },
    headers: authHeaders(),
  });
  const uploadId = initRes.data.upload_id;
  let sent = 0;
  for (let start = 0; start < file.size; start += CHUNK_SIZE) {
    const chunk = file.slice(start, start + CHUNK_SIZE);
    let attempt = 0;
    for (;;) {
      try {
        await axios.post(`${API}/admin/videos/upload/${uploadId}/chunk`, chunk, {
          headers: { ...authHeaders(), "Content-Type": "application/octet-stream", "X-Chunk-Offset": String(start) },
          timeout: 180000,
        });
        break;
      } catch (err) {
        attempt += 1;
        if (attempt >= 4) throw err;
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
    sent += chunk.size;
    onProgress(Math.round((sent / file.size) * 100));
  }
  const fin = await axios.post(`${API}/admin/videos/upload/${uploadId}/finish`, null, {
    headers: authHeaders(),
    timeout: 60000,
  });
  // Server now finalizes big files in the background — poll until ready
  const videoId = fin.data.id;
  onProgress(100);
  const deadline = Date.now() + 15 * 60 * 1000;
  for (;;) {
    await new Promise((r) => setTimeout(r, 4000));
    const list = await axios.get(`${API}/admin/videos/${courseId}`, { headers: authHeaders() });
    const v = (list.data || []).find((x) => x.id === videoId);
    if (v && v.status === "ready") return v;
    if (v && v.status === "error") throw new Error(v.error_message || "finalize_failed");
    if (Date.now() > deadline) throw new Error("finalize_timeout");
  }
};

const VideosManager = ({ courseId }) => {
  const { t } = useAdminT();
  const [videos, setVideos] = useState([]);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [teaserFile, setTeaserFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const rename = async (id) => {
    const title = renameValue.trim();
    if (!title) {
      toast.error(t("اكتبي العنوان الجديد"));
      return;
    }
    try {
      await axios.put(`${API}/admin/videos/${id}`, { title }, { headers: authHeaders() });
      toast.success(t("تمت إعادة التسمية"));
      setRenamingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const load = () =>
    axios.get(`${API}/admin/videos/${courseId}`, { headers: authHeaders() }).then((res) => setVideos(res.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Live-refresh while any upload is being finalized server-side
  useEffect(() => {
    if (!videos.some((v) => v.status === "processing")) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  const retryFinalize = async (uploadId) => {
    try {
      await axios.post(`${API}/admin/videos/upload/${uploadId}/finish`, null, { headers: authHeaders(), timeout: 60000 });
      toast.success(t("بدأت إعادة الحفظ"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("تعذر الرفع — أعيدي المحاولة"));
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error(t("اختاري ملف MP4 أو PDF"));
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      await uploadResilient(file, courseId, title, null, setProgress);
      toast.success(t("تم الرفع بنجاح"));
      setTitle("");
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("تعذر الرفع — أعيدي المحاولة"));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= videos.length) return;
    const next = [...videos];
    [next[i], next[j]] = [next[j], next[i]];
    setVideos(next);
    try {
      await axios.post(`${API}/admin/videos/reorder`, { course_id: courseId, ids: next.map((v) => v.id) }, { headers: authHeaders() });
    } catch {
      load();
    }
  };

  const toggleHide = async (v) => {
    try {
      await axios.put(`${API}/admin/videos/${v.id}`, { hidden: !v.hidden }, { headers: authHeaders() });
      toast.success(v.hidden ? t("أصبح ظاهراً للمشتركات") : t("أصبح مخفياً عن المشتركات"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/videos/${id}`, { headers: authHeaders() });
    toast.success(t("تم حذف الفيديو"));
    load();
  };

  const teaser = videos.find((v) => v.kind === "teaser");

  const uploadTeaser = async () => {
    if (!teaserFile) {
      toast.error(t("اختاري ملف MP4 للمقدمة"));
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      await uploadResilient(teaserFile, courseId, "مقدمة الدورة", "teaser", setProgress);
      toast.success(t("تم رفع الفيديو التعريفي"));
      setTeaserFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("تعذر الرفع — أعيدي المحاولة"));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="border-t border-luxury-border pt-6" data-testid="videos-manager">
      <p className="text-luxury-gold text-sm mb-2 flex items-center gap-2">
        <Film className="w-4 h-4" strokeWidth={1.5} />
        {t("فيديوهات وملفات الدورة (MP4 / PDF) — ارفعي من جهازك بلا حدود")}
      </p>
      <p className="text-luxury-gray text-xs mb-5 leading-relaxed">
        {t("أنتَ المشرف: الرفع والحذف والتعديل متاحة لك دائماً. الحماية تنطبق على المشاهِدين فقط — تُبث لهم المشاهدة داخل الموقع ولا يظهر لهم أي زر تحميل، مع علامة مائية ببريد المشاهِد تردع التصوير.")}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
        <Field label={t("عنوان الفيديو")}><input data-testid="video-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder={t("مثال: الدرس الأول")} /></Field>
        <Field label={t("ملف MP4 أو PDF")}><input data-testid="video-file-input" type="file" accept="video/mp4,.mp4,application/pdf,.pdf" onChange={(e) => setFile(e.target.files[0])} className={`${inputCls} file:ml-3 file:bg-luxury-gold file:text-luxury-black file:border-0 file:px-4 file:py-1.5 file:rounded-full file:text-sm`} /></Field>
        <button data-testid="video-upload-button" type="button" onClick={upload} disabled={uploading}
          className="flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" strokeWidth={1.5} />}
          {uploading ? `${t("جارٍ الرفع...")} ${progress}%` : t("رفع الفيديو")}
        </button>
      </div>
      {uploading && (
        <div data-testid="video-upload-progress" className="h-1.5 bg-luxury-black border border-luxury-border rounded-full overflow-hidden mb-6">
          <div className="h-full bg-luxury-gold transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}

      <div className="border border-luxury-gold/30 bg-luxury-black/40 p-5 mb-8" data-testid="teaser-manager">
        <p className="text-luxury-bone text-sm font-semibold mb-1">{t("الفيديو التعريفي القصير (Teaser)")}</p>
        <p className="text-luxury-gray text-xs mb-4 leading-relaxed">
          {t("مقطع قصير يظهر مجاناً للزائرات داخل «تفاصيل الدورة» ليتعرّفن على محتواها قبل الشراء — بدون تسجيل دخول.")}
        </p>
        {teaser ? (
          <div className="flex items-center justify-between border border-luxury-border p-4">
            <p className="text-luxury-bone text-sm flex items-center gap-3">
              <span className="text-[10px] tracking-wider px-2 py-0.5 rounded-full border border-luxury-gold text-luxury-gold">{t("مقدمة")}</span>
              {teaser.title} <span className="text-luxury-gray">({Math.round((teaser.size || 0) / 1024 / 1024)}MB)</span>
            </p>
            <button data-testid="teaser-delete" type="button" onClick={() => remove(teaser.id)} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("حذف")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <Field label={t("ملف المقدمة (MP4 قصير)")}><input data-testid="teaser-file-input" type="file" accept="video/mp4,.mp4" onChange={(e) => setTeaserFile(e.target.files[0])} className={`${inputCls} file:ml-3 file:bg-luxury-gold file:text-luxury-black file:border-0 file:px-4 file:py-1.5 file:rounded-full file:text-sm`} /></Field>
            <button data-testid="teaser-upload-button" type="button" onClick={uploadTeaser} disabled={uploading}
              className="flex items-center justify-center gap-2 border border-luxury-gold text-luxury-gold font-semibold px-6 py-3 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors disabled:opacity-60">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" strokeWidth={1.5} />}
              {uploading ? `${t("جارٍ الرفع...")} ${progress}%` : t("رفع المقدمة")}
            </button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {videos.map((v, i) => (
          <div key={v.id} data-testid={`video-row-${i}`} className="flex items-center justify-between border border-luxury-border p-4 gap-3">
            {renamingId === v.id ? (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <input
                  data-testid={`video-rename-input-${i}`}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && rename(v.id)}
                  className={`${inputCls} flex-1 min-w-0`}
                  autoFocus
                />
                <button type="button" data-testid={`video-rename-save-${i}`} onClick={() => rename(v.id)} className="p-2 text-luxury-gold hover:text-luxury-goldLight transition-colors shrink-0" aria-label={t("حفظ")}>
                  <Check className="w-5 h-5" strokeWidth={2} />
                </button>
                <button type="button" data-testid={`video-rename-cancel-${i}`} onClick={() => setRenamingId(null)} className="p-2 text-luxury-gray hover:text-luxury-bone transition-colors shrink-0" aria-label={t("إلغاء")}>
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <p className="text-luxury-bone text-sm flex items-center gap-3 min-w-0">
                <span className={`text-[10px] tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${v.kind === "pdf" ? "border-luxury-gold text-luxury-gold" : "border-luxury-border text-luxury-gray"}`}>
                  {v.kind === "pdf" ? "PDF" : v.kind === "teaser" ? t("مقدمة") : t("فيديو")}
                </span>
                <span className="truncate">{v.title}</span>
                {v.hidden && <span className="text-[10px] text-red-400 border border-red-400/50 px-2 py-0.5 rounded-full shrink-0">{t("مخفي")}</span>}
                {v.status === "processing" && (
                  <span data-testid={`video-processing-${i}`} className="flex items-center gap-1.5 text-[10px] text-luxury-gold border border-luxury-gold/50 px-2 py-0.5 rounded-full shrink-0">
                    <Loader2 className="w-3 h-3 animate-spin" /> {t("جاري الحفظ…")}
                  </span>
                )}
                {v.status === "error" && (
                  <span data-testid={`video-error-${i}`} className="text-[10px] text-red-400 border border-red-400/50 px-2 py-0.5 rounded-full shrink-0">{t("فشل الحفظ")}</span>
                )}
                <span className="text-luxury-gray shrink-0">({Math.round((v.size || 0) / 1024 / 1024)}MB)</span>
              </p>
            )}
            <div className="flex items-center gap-1 shrink-0">
              {v.status === "error" && v.upload_id && (
                <button type="button" data-testid={`video-retry-${i}`} onClick={() => retryFinalize(v.upload_id)} className="text-luxury-gold hover:text-luxury-goldLight text-xs border border-luxury-gold/50 rounded-full px-3 py-1.5 transition-colors">
                  {t("إعادة الحفظ")}
                </button>
              )}
              {v.kind !== "pdf" && v.status !== "processing" && (
                <button type="button" data-testid={`video-preview-${i}`} onClick={() => setPreview(v)} className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors" aria-label={t("معاينة")}>
                  <Play className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
              <button type="button" data-testid={`video-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors disabled:opacity-30" aria-label={t("نقل لأعلى")}>
                <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button type="button" data-testid={`video-down-${i}`} onClick={() => move(i, 1)} disabled={i === videos.length - 1} className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors disabled:opacity-30" aria-label={t("نقل لأسفل")}>
                <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
              </button>
              {renamingId !== v.id && (
                <button type="button" data-testid={`video-rename-${i}`} onClick={() => { setRenamingId(v.id); setRenameValue(v.title); }} className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors" aria-label={t("إعادة تسمية")}>
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
              <button type="button" data-testid={`video-hide-${i}`} onClick={() => toggleHide(v)} className={`p-2 transition-colors ${v.hidden ? "text-red-400 hover:text-red-300" : "text-luxury-gray hover:text-luxury-gold"}`} aria-label={v.hidden ? t("إظهار") : t("إخفاء")}>
                {v.hidden ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
              <button type="button" data-testid={`video-delete-${i}`} onClick={() => remove(v.id)} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1 p-2">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("حذف")}
              </button>
            </div>
          </div>
        ))}
        {videos.length === 0 && <p className="text-luxury-gray text-sm">{t("لا توجد فيديوهات أو ملفات بعد في هذه الدورة")}</p>}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="video-preview-modal">
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-luxury-bone font-semibold">{preview.title}</p>
              <button type="button" data-testid="video-preview-close" onClick={() => setPreview(null)} className="p-2 text-luxury-gray hover:text-luxury-bone transition-colors" aria-label={t("إغلاق")}>
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <video
              data-testid="video-preview-player"
              controls
              autoPlay
              controlsList="nodownload"
              className="w-full aspect-video bg-black border border-luxury-border"
              src={`${API}/videos/${preview.id}/stream?auth=${localStorage.getItem("themaya_token")}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const CoursesTab = () => {
  const { t } = useAdminT();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const load = () =>
    axios.get(`${API}/courses`).then((res) => setCourses(res.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const startEdit = (c) => {
    setForm({
      title: c.title, subtitle: c.subtitle, description: c.description, lessons: c.lessons, level: c.level, tag: c.tag, image: c.image || "",
      title_en: c.title_en || "", subtitle_en: c.subtitle_en || "", description_en: c.description_en || "",
      lessons_en: c.lessons_en || "", level_en: c.level_en || "", tag_en: c.tag_en || "",
      price: c.price || 0, currency: c.currency || "usd", access_days: c.access_days || 0, payment_link: c.payment_link || "", special_price: c.special_price || 0,
    });
    setEditingId(c.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, special_price: Number(form.special_price) || 0, access_days: Number(form.access_days) || 0 };
      if (editingId) {
        await axios.put(`${API}/admin/courses/${editingId}`, payload, { headers: authHeaders() });
        toast.success(t("تم تحديث الدورة"));
      } else {
        await axios.post(`${API}/admin/courses`, payload, { headers: authHeaders() });
        toast.success(t("تمت إضافة الدورة"));
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("حذف هذه الدورة نهائياً؟"))) return;
    try {
      await axios.delete(`${API}/admin/courses/${id}`, { headers: authHeaders() });
      toast.success(t("تم الحذف"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const autoTranslate = async () => {
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/admin/translate`, {
        title: form.title, subtitle: form.subtitle, description: form.description,
        lessons: form.lessons, level: form.level, tag: form.tag,
      }, { headers: authHeaders() });
      if (res.data && Object.keys(res.data).length) {
        setForm({ ...form, ...res.data });
        toast.success(t("تمت الترجمة — راجعي الحقول الإنجليزية ثم احفظي"));
      } else {
        toast.error(t("تعذرت الترجمة — أعيدي المحاولة"));
      }
    } catch {
      toast.error(t("تعذرت الترجمة — أعيدي المحاولة"));
    } finally {
      setTranslating(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-courses-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl text-luxury-bone">{t("الدورات والخدمات")} ({courses.length})</h2>
        <button
          data-testid="course-add-button"
          onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {t("إضافة دورة جديدة")}
        </button>
      </div>

      {showForm && (
        <form data-testid="course-form" onSubmit={save} className="bg-luxury-surface border border-luxury-border p-8 mb-10 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-luxury-gold">{editingId ? t("تعديل الدورة") : t("دورة جديدة")}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-luxury-gray hover:text-luxury-bone" aria-label={t("إغلاق")}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("اسم الدورة *")}><input data-testid="course-title-input" value={form.title} onChange={set("title")} className={inputCls} required /></Field>
            <Field label={t("العنوان الفرعي")}><input data-testid="course-subtitle-input" value={form.subtitle} onChange={set("subtitle")} className={inputCls} /></Field>
            <Field label={t("عدد الدروس / النوع")}><input data-testid="course-lessons-input" value={form.lessons} onChange={set("lessons")} className={inputCls} placeholder={t("مثال: ٦ دروس مسجلة")} /></Field>
            <Field label={t("المستوى")}><input data-testid="course-level-input" value={form.level} onChange={set("level")} className={inputCls} placeholder={t("مثال: لجميع المستويات")} /></Field>
            <Field label={t("الوسم (Tag)")}><input data-testid="course-tag-input" value={form.tag} onChange={set("tag")} className={inputCls} placeholder={t("مثال: كورس / ورشة / VIP")} /></Field>
          </div>
          <ImageField testid="course-image-field" label={t("صورة الدورة (اختياري)")} value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
          <Field label={t("وصف الدورة")}><textarea data-testid="course-description-input" value={form.description} onChange={set("description")} rows={3} className={inputCls} /></Field>

          <div className="border-t border-luxury-border pt-6">
            <p className="text-luxury-gold text-sm mb-5">{t("البيع والوصول — Pricing &amp; Access")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field label={t("السعر (0 = تسجيل عبر واتساب)")}><input data-testid="course-price-input" type="number" min="0" step="0.01" dir="ltr" value={form.price} onChange={set("price")} className={inputCls} /></Field>
              <Field label={t("رابط الدفع المباشر (Stripe Payment Link — اختياري)")}><input data-testid="course-payment-link-input" dir="ltr" value={form.payment_link} onChange={set("payment_link")} className={inputCls} placeholder="https://buy.stripe.com/..." /></Field>
              <Field label={t("السعر الخاص (0 = بلا عرض)")}><input data-testid="course-special-price-input" type="number" min="0" step="0.01" dir="ltr" value={form.special_price} onChange={set("special_price")} className={inputCls} /></Field>
              <Field label={t("العملة")}>
                <select data-testid="course-currency-select" dir="ltr" value={form.currency} onChange={set("currency")} className={inputCls}>
                  <option value="usd">USD</option>
                  <option value="aed">AED</option>
                  <option value="sar">SAR</option>
                  <option value="kwd">KWD</option>
                  <option value="eur">EUR</option>
                </select>
              </Field>
              <Field label={t("مدة الوصول — قرار المشرف لكل دورة")}>
                <div className="flex gap-2">
                  <button type="button" data-testid="course-access-lifetime" onClick={() => setForm({ ...form, access_days: 0 })}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-full text-sm font-semibold border transition-colors ${Number(form.access_days) > 0 ? "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold" : "bg-luxury-gold text-luxury-black border-luxury-gold"}`}>
                    <InfinityIcon className="w-4 h-4" strokeWidth={1.5} /> {t("مدى الحياة")}
                  </button>
                  <button type="button" data-testid="course-access-limited" onClick={() => setForm({ ...form, access_days: Number(form.access_days) || 30 })}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-full text-sm font-semibold border transition-colors ${Number(form.access_days) > 0 ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
                    <Clock className="w-4 h-4" strokeWidth={1.5} /> {t("فترة محددة")}
                  </button>
                </div>
                {Number(form.access_days) > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <input data-testid="course-access-days-input" type="number" min="1" dir="ltr" value={form.access_days} onChange={set("access_days")} className={inputCls} placeholder={t("عدد الأيام")} />
                    <span className="text-luxury-gray text-xs whitespace-nowrap">{t("يوم — يحددها المشرف فقط")}</span>
                  </div>
                )}
              </Field>
            </div>
            {form.payment_link && editingId && (
              <p data-testid="course-stripe-redirect-hint" className="mt-4 text-xs text-luxury-gray leading-relaxed border border-luxury-border p-3">
                {t("بعد إنشاء رابط الدفع في Stripe، فعّلي خيار «Redirect after payment» والصقي هذا الرابط كما هو — Stripe يستبدل الجزء الأخير تلقائياً، والوصول يُفتح فوراً بلا مراجعة يدوية:")}{" "}
                <code dir="ltr" className="text-luxury-gold break-all">{window.location.origin}/payment/success?course={editingId}{"&session_id={CHECKOUT_SESSION_ID}"}</code>
              </p>
            )}
          </div>

          <div className="border-t border-luxury-border pt-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <p className="text-luxury-gold text-sm">{t("النسخة الإنجليزية — تُملأ تلقائياً عند الحفظ إذا تُركت فارغة")}</p>
              <button type="button" data-testid="course-translate-button" onClick={autoTranslate} disabled={translating}
                className="flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold text-xs font-semibold px-4 py-2 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors disabled:opacity-60">
                {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {t("ترجمة تلقائية من العربية")}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Course title (EN)"><input dir="ltr" data-testid="course-title-en-input" value={form.title_en} onChange={set("title_en")} className={inputCls} /></Field>
              <Field label="Subtitle (EN)"><input dir="ltr" value={form.subtitle_en} onChange={set("subtitle_en")} className={inputCls} /></Field>
              <Field label="Lessons (EN)"><input dir="ltr" value={form.lessons_en} onChange={set("lessons_en")} className={inputCls} placeholder="e.g. 6 recorded lessons" /></Field>
              <Field label="Level (EN)"><input dir="ltr" value={form.level_en} onChange={set("level_en")} className={inputCls} placeholder="e.g. All levels" /></Field>
              <Field label="Tag (EN)"><input dir="ltr" value={form.tag_en} onChange={set("tag_en")} className={inputCls} placeholder="e.g. Course / Workshop / VIP" /></Field>
            </div>
            <div className="mt-5">
              <Field label="Description (EN)"><textarea dir="ltr" value={form.description_en} onChange={set("description_en")} rows={3} className={inputCls} /></Field>
            </div>
          </div>

          {editingId ? (
            <VideosManager courseId={editingId} />
          ) : (
            <div className="border-t border-luxury-border pt-6" data-testid="videos-manager-hint">
              <p className="text-luxury-gray text-sm flex items-center gap-2">
                <Film className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                {t("احفظي الدورة أولاً، ثم اضغطي «فيديوهات» بجانبها لرفع ملفات MP4 غير محدودة")}
              </p>
            </div>
          )}

          <button data-testid="course-save-button" type="submit" disabled={saving} className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editingId ? t("حفظ التعديلات") : t("إضافة الدورة")}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {courses.map((c, i) => (
          <div key={c.id} data-testid={`admin-course-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5 flex flex-wrap items-center gap-4 justify-between">
            <div>
              <p className="text-luxury-bone font-semibold text-lg">{c.title}</p>
              <p className="text-luxury-gray text-sm">
                {c.subtitle} — {c.tag}
                {c.price > 0 ? ` — ${c.price} ${(c.currency || "usd").toUpperCase()}` : ""}
                {c.access_days > 0 ? ` — ${t("وصول")} ${c.access_days} ${t("يوم")}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`admin-course-duplicate-${i}`} onClick={async () => {
                try {
                  await axios.post(`${API}/admin/courses/${c.id}/duplicate`, {}, { headers: authHeaders() });
                  toast.success(t("تم نسخ الدورة مع كل ملفاتها — تجدينها غير منشورة"));
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                }
              }} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <Copy className="w-4 h-4" strokeWidth={1.5} /> {t("نسخة")}
              </button>
              <button data-testid={`admin-course-copy-link-${i}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/course/${c.id}`); toast.success(t("تم نسخ رابط الدورة")); }} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <LinkIcon className="w-4 h-4" strokeWidth={1.5} /> {t("نسخ الرابط")}
              </button>
              <button data-testid={`admin-course-join-link-${i}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join/${c.id}`); toast.success(t("تم نسخ رابط الانضمام")); }} className="flex items-center gap-2 border border-luxury-gold/50 text-luxury-gold px-4 py-2 rounded-full text-sm hover:bg-luxury-gold hover:text-luxury-black transition-colors">
                <Lock className="w-4 h-4" strokeWidth={1.5} /> {t("رابط الانضمام")}
              </button>
              <button data-testid={`admin-course-videos-${i}`} onClick={() => startEdit(c)} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <Film className="w-4 h-4" strokeWidth={1.5} /> {t("فيديوهات")}
              </button>
              <button data-testid={`admin-course-edit-${i}`} onClick={() => startEdit(c)} className="flex items-center gap-2 border border-luxury-border text-luxury-gold px-4 py-2 rounded-full text-sm hover:border-luxury-gold transition-colors">
                <Pencil className="w-4 h-4" strokeWidth={1.5} /> {t("تعديل")}
              </button>
              <button data-testid={`admin-course-delete-${i}`} onClick={() => remove(c.id)} className="flex items-center gap-2 border border-luxury-border text-red-400 px-4 py-2 rounded-full text-sm hover:border-red-400 transition-colors">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("حذف")}
              </button>
            </div>
          </div>
        ))}
        {courses.length === 0 && <p className="text-luxury-gray text-center py-10">{t("لا توجد دورات بعد — أضيفي أول دورة")}</p>}
      </div>
    </div>
  );
};
